import {
  Checkbox,
  createStyles,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import Select from "@material-ui/core/Select";
import React from "react";
import { useIntl } from "react-intl";
import { ActionMeta, ValueType } from "react-select";
import {
  CourseCertificateValidity,
  CourseCertificateValidityTranslationKey,
  CourseType,
  CourseTypeEnumValues,
  CourseTypeTranslationKey,
  CourseViewModel,
  RequiredCourse,
} from "../../model/Course";
import { GetLocalizedName, ModuleIndex } from "../../model/Module";
import { GetTranslation } from "../../translations/GetTranslation";
import GridNumberField from "../common/customGrid/GridNumberField";
import GridTextField from "../common/customGrid/GridTextField";
import CustomSelect, { OptionType } from "../common/customSelect/CustomSelect";
import TranslatedMessage from "../translations/TranslatedMessage";

type CourseFormFieldsProps = {
  course: CourseViewModel;
  isCreation: boolean;
  handleFieldChange: (change: Partial<CourseViewModel>) => void;
  isReadOnly?: boolean;
  requiredFieldStatus: RequiredCourse;
};

const useStyles = makeStyles(() =>
  createStyles({
    form: {
      paddingTop: "12px !important",
      paddingBottom: "0px !important",
    },
  })
);

const CourseFormFields = (props: CourseFormFieldsProps) => {
  const { course, isCreation, handleFieldChange, isReadOnly, requiredFieldStatus } = props;
  const intl = useIntl();
  const classes = useStyles({});
  const getModulesAsOptionTypes = (modules: ModuleIndex[]) => {
    return modules.map((module) => ({
      value: module.id,
      label: GetLocalizedName(module),
    }));
  };

  const getSelectedModulesAsOptionTypes = () => {
    const { modulesRelevantForGrade } = course;

    return modulesRelevantForGrade.map((moduleSelected) => {
      const module = course.modules.find((module) => module.id === moduleSelected);
      return {
        value: moduleSelected,
        label: module ? GetLocalizedName(module) : moduleSelected,
      };
    });
  };

  const onModuleRevelantSelectChange = (
    selectedElements: ValueType<OptionType>,
    actionMeta?: ActionMeta<OptionType>
  ) => {
    const selectedElementsArray = selectedElements as OptionType[] | undefined;
    const modulesRelevantForGrade =
      (actionMeta && actionMeta.action === "clear") || !selectedElementsArray || selectedElementsArray.length === 0
        ? []
        : [...selectedElementsArray.map((selectedElement) => selectedElement.value)];

    handleFieldChange({
      modulesRelevantForGrade,
    });
  };

  const selectedCoursesAsOptionTypes = getSelectedModulesAsOptionTypes();
  const modulesOptions = getModulesAsOptionTypes(course.modules);
  const availableCourseTypes = CourseTypeEnumValues;

  return (
    <div>
      <Grid container={true} spacing={4}>
        <GridTextField
          required
          error={requiredFieldStatus.isNameDEEmpty}
          label={GetTranslation(intl, "course.field.nameDE")}
          xs={12}
          md={6}
          onChange={(event) => handleFieldChange({ nameDE: event.target.value })}
          value={course.nameDE}
          readOnly={isReadOnly}
        />
        <GridTextField
          required
          error={requiredFieldStatus.isNameENEmpty}
          label={GetTranslation(intl, "course.field.nameEN")}
          xs={12}
          md={6}
          onChange={(event) => handleFieldChange({ nameEN: event.target.value })}
          value={course.nameEN}
          readOnly={isReadOnly}
        />
        <GridTextField
          required
          error={requiredFieldStatus.isNameFREmpty}
          label={GetTranslation(intl, "course.field.nameFR")}
          xs={12}
          md={6}
          onChange={(event) => handleFieldChange({ nameFR: event.target.value })}
          value={course.nameFR}
          readOnly={isReadOnly}
        />
        <GridTextField
          required
          error={requiredFieldStatus.isNameITEmpty}
          label={GetTranslation(intl, "course.field.nameIT")}
          xs={12}
          md={6}
          onChange={(event) => handleFieldChange({ nameIT: event.target.value })}
          value={course.nameIT}
          readOnly={isReadOnly}
        />
        {requiredFieldStatus.isModuleNumbersZero && (
          <Grid item xs={12} style={{ paddingBottom: 0 }}>
            <Typography variant="body2" color="error">
              <TranslatedMessage id="course.validation.numberOfAnyModulesIsZero" />
            </Typography>
          </Grid>
        )}
        <GridNumberField
          label={GetTranslation(intl, "course.field.numberOfMandatoryModules")}
          xs={12}
          md={4}
          onChange={(value: number) => handleFieldChange({ numberOfMandatoryModules: value })}
          value={course.numberOfMandatoryModules}
          min={0}
          error={false}
          readOnly={isReadOnly}
        />
        <GridNumberField
          label={GetTranslation(intl, "course.field.numberOfSelectionModules")}
          xs={12}
          md={4}
          onChange={(value: number) => handleFieldChange({ numberOfSelectionModules: value })}
          value={course.numberOfSelectionModules}
          min={0}
          error={false}
          readOnly={isReadOnly}
        />

        <GridNumberField
          label={GetTranslation(intl, "course.field.numberOfProcessModules")}
          xs={12}
          md={4}
          onChange={(value: number) => handleFieldChange({ numberOfProcessModules: value })}
          value={course.numberOfProcessModules}
          min={0}
          error={false}
          readOnly={isReadOnly}
        />

        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <FormLabel style={{ fontSize: "0.75rem" }}>
              <TranslatedMessage id="course.field.modulesValidity" />
            </FormLabel>
            <Grid container direction="row" justify="flex-start" alignItems="center" wrap="nowrap">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={course.modulesValidity === null}
                    onChange={(event) => {
                      handleFieldChange({ modulesValidity: event.target.checked ? null : 0 });
                    }}
                  />
                }
                label={<TranslatedMessage id="course.field.modulesValidity.unlimited" />}
                labelPlacement="end"
                style={{ minWidth: "50%", margin: 0, fontSize: "0.9rem" }}
                disabled={isReadOnly}
              />
              <Grid item container direction="row" justify="flex-start" alignItems="center">
                <TextField
                  type="number"
                  value={course.modulesValidity}
                  InputProps={{
                    inputProps: { min: 0 },
                    readOnly: isReadOnly,
                  }}
                  style={{ maxWidth: "120px" }}
                  disabled={course.modulesValidity === null}
                  onChange={(event) => {
                    if (event.target.value === "") {
                      handleFieldChange({ modulesValidity: 0 });
                      return;
                    }
                    const parsed = parseInt(event.target.value);
                    if (!Object.is(NaN, parsed)) {
                      handleFieldChange({ modulesValidity: parsed });
                    }
                  }}
                />
                <span style={{ marginLeft: "10px", fontSize: "0.85rem" }}>
                  <TranslatedMessage id="course.field.modulesValidity.days" />
                </span>
              </Grid>
            </Grid>
          </FormControl>
        </Grid>

        <Grid item={true} xs={12} md={4}>
          <FormControl fullWidth={true}>
            <InputLabel>
              <TranslatedMessage id="course.field.certificateValidity" />
            </InputLabel>
            <Select
              value={course.certificateValidity}
              onChange={(event) =>
                handleFieldChange({ certificateValidity: event.target.value as CourseCertificateValidity })
              }
              readOnly={isReadOnly}
            >
              <MenuItem value={CourseCertificateValidity.Unlimited}>
                <TranslatedMessage id={CourseCertificateValidityTranslationKey[CourseCertificateValidity.Unlimited]} />
              </MenuItem>
              <MenuItem value={CourseCertificateValidity.FiveYears}>
                <TranslatedMessage id={CourseCertificateValidityTranslationKey[CourseCertificateValidity.FiveYears]} />
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {!isCreation && (
          <Grid item={true} xs={12} md={4}>
            <FormControl fullWidth={true}>
              <CustomSelect
                required={false}
                value={selectedCoursesAsOptionTypes}
                name="course-select"
                label=""
                placeholder={<TranslatedMessage id="course.field.moduleRelevantForGrade" />}
                options={modulesOptions}
                isMulti={true}
                onChange={onModuleRevelantSelectChange}
                className={classes.form}
                isDisabled={isReadOnly}
              />
            </FormControl>
          </Grid>
        )}
        <Grid item={true} xs={12} md={4}>
          <FormControl fullWidth={true}>
            <InputLabel>
              <TranslatedMessage id="course.type" />
            </InputLabel>
            <Select
              onChange={(event) => handleFieldChange({ type: event.target.value as CourseType })}
              readOnly={isReadOnly}
              required
              error={requiredFieldStatus.isTypeEmpty}
              value={course.type}
            >
              {availableCourseTypes.map((type, typeIndex) => (
                <MenuItem value={type} key={typeIndex}>
                  <TranslatedMessage id={CourseTypeTranslationKey[type]} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </div>
  );
};

export default CourseFormFields;
